export const OPEN_JOB_ADDRESS_NEEDS_FIXING = 'Address needs fixing';

function plainObject(value: unknown): Record<string, unknown> | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    return value as Record<string, unknown>;
}

function nonBlankString(value: unknown): boolean {
    return typeof value === 'string' && value.trim().length > 0;
}

/** Usable when `address` is an object with a non-blank street or city. */
export function shopAddressIsUsable(address: unknown): boolean {
    try {
        const record = plainObject(address);
        if (!record) return false;
        return nonBlankString(record.street) || nonBlankString(record.city);
    } catch {
        return false;
    }
}

/** Finite number, or a non-blank numeric string. Blank and non-numeric values do not parse. */
function parsedShopCoordinate(value: unknown): number | null {
    try {
        if (typeof value === 'number') return Number.isFinite(value) ? value : null;
        if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
            return Number(value);
        }
        return null;
    } catch {
        return null;
    }
}

function ownLatLng(record: Record<string, unknown> | null): { lat: unknown; lng: unknown } | null {
    if (!record) return null;
    if (!Object.prototype.hasOwnProperty.call(record, 'lat') || !Object.prototype.hasOwnProperty.call(record, 'lng')) {
        return null;
    }
    return { lat: record.lat, lng: record.lng };
}

/**
 * Failed-geocode default: the shop carries a lat/lng pair and both parse to 0.
 * Top-level lat/lng win. A shop with neither pair is not this case.
 * One zero, or a pair that does not both parse to 0, is not this case.
 */
function shopCoordinatesAreZeroZero(shop: Record<string, unknown>): boolean {
    try {
        const pair = ownLatLng(shop) ?? ownLatLng(plainObject(shop.location));
        if (!pair) return false;
        return parsedShopCoordinate(pair.lat) === 0 && parsedShopCoordinate(pair.lng) === 0;
    } catch {
        return true;
    }
}

type ShopRead =
    | { kind: 'absent' }
    | { kind: 'object'; shop: Record<string, unknown> }
    | { kind: 'unreadable' };

/** A shop id string, null, or a missing shop is not shop-address data. */
function readShop(value: unknown): ShopRead {
    try {
        const record = plainObject(value);
        if (!record || !Object.prototype.hasOwnProperty.call(record, 'shop')) return { kind: 'absent' };
        const shop = plainObject(record.shop);
        if (!shop) return { kind: 'absent' };
        return { kind: 'object', shop };
    } catch {
        return { kind: 'unreadable' };
    }
}

/**
 * True when the invite payload or the job payload includes a shop object whose
 * address is missing, or is not an object with at least street or city, or whose
 * coordinates are lat 0 and lng 0 after numeric parsing.
 * A shop object with no lat/lng pair keeps the street-or-city rule. One zero stays valid.
 * When neither payload carries a shop object, returns false so accept stays unchanged.
 * Callers must not read Firestore to supply an address the payload does not carry.
 */
export function openJobShopAddressNeedsFixing(invite: unknown, job: unknown): boolean {
    try {
        const inviteRecord = plainObject(invite);
        const reads = [readShop(invite), readShop(job), readShop(inviteRecord ? inviteRecord.job : null)];
        if (reads.some((read) => read.kind === 'unreadable')) return true;
        const present = reads.filter(
            (read): read is { kind: 'object'; shop: Record<string, unknown> } => read.kind === 'object',
        );
        if (present.length === 0) return false;
        return present.some((read) => {
            try {
                if (shopCoordinatesAreZeroZero(read.shop)) return true;
                return !shopAddressIsUsable(read.shop.address);
            } catch {
                return true;
            }
        });
    } catch {
        return true;
    }
}
